import React from "react";
import { Shield, Award, Clock, DollarSign, Heart, Users, ArrowRight, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { useQuoteModal } from "@/hooks/useQuoteModal";

const features = [
  {
    icon: Clock,
    title: "Fast & Flexible",
    description: "Same-day and next-day appointments across Davie & Broward County — we show up on time!"
  },
  {
    icon: DollarSign,
    title: "Upfront Pricing",
    description: "No hidden fees, no fuel surcharges, no surprise fees. You know the exact price before we lift a finger."
  },
  {
    icon: Shield,
    title: "Licensed & Insured",
    description: "Full commercial general liability protection on every residential and commercial property cleanout."
  },
  {
    icon: Heart,
    title: "Eco-Friendly Recycling",
    description: "We sort all junk. Usable items get donated to local South Florida charities; metal & electronics are recycled."
  },
  {
    icon: Award,
    title: "5+ Years Strong",
    description: "Proven local reputation in Davie with 5-star reviews from real neighbors."
  },
  {
    icon: Users,
    title: "Friendly Heavy Lifters",
    description: "Polite, energetic, respectful crew members who treat your home and walls with care."
  }
];

export const AboutSection: React.FC = () => {
  const { openModal } = useQuoteModal();
  return (
    <section id="about" className="py-20 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Upper Grid: About Text + Heavy Duty Image */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          
          {/* Left Column: Image Stack */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-slate-100">
              <picture>
                <source 
                  media="(max-width: 640px)" 
                  srcSet="https://vibe.filesafe.space/1786557593842074307/assets/8367f1cf-1edf-4edc-a51b-32db1d0929a7.png?w=360&q=70 360w, https://vibe.filesafe.space/1786557593842074307/assets/8367f1cf-1edf-4edc-a51b-32db1d0929a7.png?w=480&q=75 480w" 
                  sizes="(max-width: 640px) 100vw, 50vw"
                />
                <img
                  src="https://vibe.filesafe.space/1786557593842074307/assets/8367f1cf-1edf-4edc-a51b-32db1d0929a7.png?w=400&q=75"
                  alt="Muscle Junk Removal crew loading old furniture and stove into professional dual-axle dump trailer in Davie Florida"
                  className="w-full h-[400px] sm:h-[480px] object-cover"
                  loading="lazy"
                  decoding="async"
                  width={500}
                  height={400}
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
            </div>

            {/* Stat Floating Badges */}
            <div className="absolute -bottom-6 -left-6 bg-primary text-white rounded-2xl p-5 shadow-2xl hidden sm:block border-2 border-white">
              <div className="text-4xl font-black font-heading leading-none">5+</div>
              <div className="text-xs font-bold uppercase tracking-wider mt-1">Years Davie Strong</div>
            </div>

            <div className="absolute -top-6 -right-6 bg-amber-400 text-slate-950 rounded-2xl p-5 shadow-2xl hidden sm:block border-2 border-white">
              <div className="text-3xl font-black font-heading leading-none">DAVIE, FL</div>
              <div className="text-xs font-bold uppercase tracking-wider mt-1">Local &amp; Family Owned</div>
            </div>
          </div>

          {/* Right Column: Story & SEO details */}
          <div>
            <div className="inline-flex items-center gap-2 bg-amber-100 text-slate-900 font-extrabold px-4 py-1.5 rounded-full text-xs uppercase tracking-widest mb-4">
              Local Heavy Lifters Since Day One
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black font-heading text-slate-950 uppercase tracking-tight mb-6">
              Built On <span className="text-primary">Muscle</span>. Backed By <span className="text-amber-500">Experience</span>.
            </h2>

            <div className="space-y-4 text-slate-700 text-base sm:text-lg leading-relaxed mb-8">
              <p>
                For over 5 years, <strong>Muscle Junk Removal</strong> has been Davie’s go-to team for fast, no-hassle junk removal and heavy property cleanouts. We’ve built our name on hard work, honest upfront pricing, and showing up ready to move heavy loads!
              </p>
              <p>
                Whether it’s an old sectional couch, a broken refrigerator, a packed 2-car garage in Shenandoah, or a commercial office suite near Nova Southeastern University, our Davie crew handles it all.
              </p>
              <p className="font-extrabold text-slate-950 text-xl border-l-4 border-primary pl-4 py-1">
                No job is too big, too heavy, or too messy in Davie FL. That’s what the muscle is for!
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" onClick={openModal} className="w-full sm:w-auto !bg-rose-600 hover:!bg-rose-700 text-white shadow-lg shadow-rose-500/30 font-extrabold uppercase tracking-wide border-0">
                  Get Free Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              <a href="tel:9542808090">
                <Button size="lg" className="w-full sm:w-auto !bg-amber-500 hover:!bg-amber-600 text-slate-950 font-extrabold uppercase tracking-wide border-0 shadow-lg shadow-amber-500/30">
                  <Phone className="w-4 h-4 mr-2" /> Call (954) 280-8090
                </Button>
              </a>
            </div>
          </div>

        </div>

        {/* Lower Grid: 6 Why Davie Trusts Us Feature Cards */}
        <div className="pt-12 border-t border-slate-200">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h3 className="text-2xl sm:text-3xl font-black font-heading text-slate-950 uppercase">
              Why Davie Homeowners &amp; Businesses Choose Us
            </h3>
            <p className="text-slate-600 text-sm mt-2">
              We earn our 5-star reviews one load at a time with honest service and hard work.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat, idx) => {
              const Icon = feat.icon;
              return (
                <div
                  key={idx}
                  className="bg-slate-50 border-2 border-slate-200 hover:border-amber-400 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg"
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="text-lg font-black text-slate-950 font-heading mb-2">
                    {feat.title}
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {feat.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};
